// Adventures of Black Emigo — 2D side-scrolling platformer engine
// Pure canvas. Procedural endless or daily-seeded levels.

export const WORLD = {
  width: 960,
  height: 540,
  gravity: 0.7,
  groundY: 460,
  tileSize: 40,
  chunkSize: 800,
};

const PLAYER_W = 28;
const PLAYER_H = 44;

// Walking enemies + bosses unified under one map.
const ENEMY_TYPES = {
  soldier: { w: 32, h: 38, color: "#4a5d3a", accent: "#2a3d1a", speed: 1.4, score: 100, hp: 1, label: "ARMY", isFlying: false },
  snake: { w: 38, h: 14, color: "#39ff14", accent: "#0d8a08", speed: 1.8, score: 80, hp: 1, label: "SNAKE", isFlying: false },
  police: { w: 30, h: 42, color: "#1a3b8e", accent: "#0a1f4a", speed: 2.2, score: 150, hp: 1, label: "POLICE", isFlying: false },
  thug: { w: 32, h: 42, color: "#7a1f1f", accent: "#3a0a0a", speed: 1.6, score: 120, hp: 1, label: "THUG", isFlying: false },
  swatvan: { w: 96, h: 50, color: "#1a1a1a", accent: "#3a3a3a", speed: 1.1, score: 600, hp: 4, label: "SWAT VAN", isFlying: false, isBoss: true },
  chopper: { w: 84, h: 36, color: "#22232a", accent: "#0a0a0a", speed: 1.2, score: 800, hp: 4, label: "CHOPPER", isFlying: true, isBoss: true },
};

const WALK_ENEMY_KEYS = ["soldier", "snake", "police", "thug"];

/**
 * Pick a walking-enemy type from a uniform `r` in [0, 1).
 * Distance-tiered weights replace nested ternaries.
 */
function pickEnemyType(distance, r) {
  if (distance < 1200) {
    return r < 0.6 ? "soldier" : "snake";
  }
  if (distance < 3500) {
    if (r < 0.4) return "soldier";
    if (r < 0.7) return "snake";
    if (r < 0.9) return "thug";
    return "police";
  }
  const idx = Math.min(
    WALK_ENEMY_KEYS.length - 1,
    Math.floor(r * WALK_ENEMY_KEYS.length),
  );
  return WALK_ENEMY_KEYS[idx];
}

// Power-ups
const POWERUP_TYPES = {
  chain: { color: "#ffcc00", label: "CHAIN" },
  shades: { color: "#0a0a0a", label: "SHADES" },
  cup: { color: "#ff003c", label: "CUP" },
};

// ----- PRNG -----
function mulberry32(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function todayString() {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function dailySeed() {
  return hashString("aobe-daily-" + todayString());
}

// ---- State ----
export function createInitialState(opts = {}) {
  const mode = opts.mode === "daily" ? "daily" : "endless";
  let seed;
  if (mode === "daily") {
    seed = dailySeed();
  } else {
    seed = opts.seed != null ? opts.seed : (Math.random() * 0xffffffff) >>> 0;
  }
  const rng = mulberry32(seed);

  return {
    mode,
    seed,
    seedDate: mode === "daily" ? todayString() : null,
    rng,
    player: {
      x: 80,
      y: WORLD.groundY - PLAYER_H,
      vx: 0,
      vy: 0,
      w: PLAYER_W,
      h: PLAYER_H,
      onGround: true,
      facing: 1,
      invulnUntil: 0,
      anim: 0,
      // ability flags
      hasDoubleJump: false,
      didDoubleJump: false,
      shadesUntil: 0,
      multiplierUntil: 0,
      jumpHeldPrev: false,
    },
    keys: { left: false, right: false, jump: false },
    camera: { x: 0 },
    platforms: [],
    groundSegments: [],
    enemies: [],
    coins: [],
    powerups: [],
    particles: [],
    floatingTexts: [], // {text, x, y, vy, life, color}
    lastSpawnX: 0,
    lastBossBlock: 0, // highest block for which we've spawned a boss
    bossActive: null, // reference to active boss enemy (if any)
    score: 0,
    coinsCollected: 0,
    enemiesDefeated: 0,
    bossesDefeated: 0,
    powerupsByType: { chain: 0, shades: 0, cup: 0 },
    distance: 0,
    lives: 3,
    state: "playing",
    blockNumber: 1,
    flashTimer: 0,
    tick: 0,
    pendingSounds: [], // drained by React layer each frame
  };
}

function rand(state, min, max) {
  return state.rng() * (max - min) + min;
}
function randInt(state, min, max) {
  return Math.floor(rand(state, min, max + 1));
}

// ---- Procedural generation ----
function spawnChunk(state, startX) {
  const segWidth = randInt(state, 360, 700);
  state.groundSegments.push({ x: startX, w: segWidth });

  let nextX = startX + segWidth;
  // Gaps after some progress
  if (state.distance > 600 && state.rng() < 0.55) {
    const gap = randInt(state, 80, 160);
    nextX += gap;
  }

  // Floating platforms
  const platformCount = randInt(state, 1, 3);
  for (let i = 0; i < platformCount; i++) {
    const pw = randInt(state, 80, 160);
    const px = startX + randInt(state, 40, Math.max(60, segWidth - pw - 40));
    const py = WORLD.groundY - randInt(state, 90, 230);
    state.platforms.push({ x: px, y: py, w: pw, h: 14 });

    // Coins above platforms
    if (state.rng() < 0.7) {
      const coinCount = randInt(state, 1, 3);
      for (let c = 0; c < coinCount; c++) {
        state.coins.push({
          x: px + 20 + c * 26,
          y: py - 28,
          collected: false,
          bob: state.rng() * Math.PI * 2,
        });
      }
    }

    // Power-up sometimes on a platform top
    if (state.distance > 400 && state.rng() < 0.18) {
      const keys = Object.keys(POWERUP_TYPES);
      const type = keys[Math.floor(state.rng() * keys.length)];
      state.powerups.push({
        type,
        x: px + pw / 2 - 9,
        y: py - 22,
        collected: false,
        bob: state.rng() * Math.PI * 2,
      });
    }
  }

  // Ground-level coin line
  if (state.rng() < 0.5) {
    const coinCount = randInt(state, 2, 5);
    const startCoinX = startX + randInt(state, 80, 200);
    for (let c = 0; c < coinCount; c++) {
      state.coins.push({
        x: startCoinX + c * 28,
        y: WORLD.groundY - 56,
        collected: false,
        bob: state.rng() * Math.PI * 2,
      });
    }
  }

  // Enemies — difficulty scales with distance
  const difficulty = Math.min(1, state.distance / 8000);
  const enemyCount = randInt(state, 1, 2 + Math.floor(difficulty * 2));
  for (let i = 0; i < enemyCount; i++) {
    const typeKey = pickEnemyType(state.distance, state.rng());
    const t = ENEMY_TYPES[typeKey];
    const ex = startX + randInt(state, 120, Math.max(140, segWidth - 80));
    state.enemies.push({
      type: typeKey,
      x: ex,
      y: WORLD.groundY - t.h,
      vx: -t.speed,
      w: t.w,
      h: t.h,
      hp: t.hp,
      maxHp: t.hp,
      alive: true,
      anim: 0,
      hitFlash: 0,
    });
  }

  state.lastSpawnX = nextX;
}

function spawnBoss(state) {
  // Determine boss type
  const idx = state.blockNumber / 5;
  const typeKey = idx % 2 === 0 ? "chopper" : "swatvan";
  const t = ENEMY_TYPES[typeKey];

  const bx = state.lastSpawnX + 200;
  // Ensure ground segment exists where boss lands (extend if needed)
  state.groundSegments.push({ x: bx - 100, w: 700 });
  state.lastSpawnX = Math.max(state.lastSpawnX, bx + 500);

  const boss = {
    type: typeKey,
    x: bx,
    y: t.isFlying ? WORLD.groundY - 220 : WORLD.groundY - t.h,
    vx: -t.speed,
    vy: 0,
    w: t.w,
    h: t.h,
    hp: t.hp,
    maxHp: t.hp,
    alive: true,
    anim: 0,
    hitFlash: 0,
    isBoss: true,
    baseY: t.isFlying ? WORLD.groundY - 220 : WORLD.groundY - t.h,
  };
  state.enemies.push(boss);
  state.bossActive = boss;
  state.lastBossBlock = state.blockNumber;
  state.floatingTexts.push({
    text: `BLOCK ${state.blockNumber} BOSS`,
    x: state.camera.x + WORLD.width / 2,
    y: 110,
    vy: 0,
    life: 90,
    color: "#ff003c",
    big: true,
  });
}

export function ensureWorldAhead(state) {
  const cameraRight = state.camera.x + WORLD.width;
  while (state.lastSpawnX < cameraRight + WORLD.chunkSize) {
    spawnChunk(state, state.lastSpawnX);
  }
  // Boss every 5 blocks
  if (
    state.blockNumber > state.lastBossBlock &&
    state.blockNumber % 5 === 0 &&
    state.blockNumber >= 5
  ) {
    spawnBoss(state);
  }
}

export function pruneBehind(state) {
  const cutoff = state.camera.x - 200;
  state.platforms = state.platforms.filter((p) => p.x + p.w > cutoff);
  state.groundSegments = state.groundSegments.filter((g) => g.x + g.w > cutoff);
  state.enemies = state.enemies.filter((e) => e.x + e.w > cutoff && e.alive);
  state.coins = state.coins.filter((c) => c.x > cutoff && !c.collected);
  state.powerups = state.powerups.filter((p) => p.x > cutoff && !p.collected);
  state.particles = state.particles.filter((p) => p.life > 0);
  state.floatingTexts = state.floatingTexts.filter((t) => t.life > 0);
  if (state.bossActive && !state.bossActive.alive) state.bossActive = null;
}

// ---- Collision ----
function aabb(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
function isOnGround(x, w, groundSegments) {
  for (const g of groundSegments) {
    if (x + w > g.x && x < g.x + g.w) return true;
  }
  return false;
}

// ---- Main update ----
export function updateState(state) {
  if (state.state !== "playing") return state;
  state.tick++;
  const p = state.player;
  const k = state.keys;

  // Horizontal movement
  const accel = 0.6;
  const maxSpeed = 4.6;
  if (k.left) {
    p.vx = Math.max(p.vx - accel, -maxSpeed);
    p.facing = -1;
  } else if (k.right) {
    p.vx = Math.min(p.vx + accel, maxSpeed);
    p.facing = 1;
  } else {
    p.vx *= 0.82;
    if (Math.abs(p.vx) < 0.05) p.vx = 0;
  }

  // Jump (with edge detection + double jump)
  const jumpEdge = k.jump && !p.jumpHeldPrev;
  if (jumpEdge) {
    if (p.onGround) {
      p.vy = -13;
      p.onGround = false;
      p.didDoubleJump = false;
      state.pendingSounds.push("jump");
    } else if (p.hasDoubleJump && !p.didDoubleJump) {
      p.vy = -12;
      p.didDoubleJump = true;
      state.pendingSounds.push("doubleJump");
      // Burst particle effect at feet
      spawnParticles(state, p.x + p.w / 2, p.y + p.h, "#ffcc00", 10);
    }
  }
  p.jumpHeldPrev = k.jump;

  // Gravity
  p.vy += WORLD.gravity;
  if (p.vy > 16) p.vy = 16;

  // X
  p.x += p.vx;
  if (p.x < state.camera.x + 10) p.x = state.camera.x + 10;

  // Y
  p.y += p.vy;

  // Platform collisions (falling)
  p.onGround = false;
  if (p.vy >= 0) {
    for (const pl of state.platforms) {
      const wasAbove = p.y + p.h - p.vy <= pl.y + 2;
      const horizOverlap = p.x + p.w > pl.x + 2 && p.x < pl.x + pl.w - 2;
      const nowIn = p.y + p.h >= pl.y && p.y + p.h <= pl.y + pl.h + 4;
      if (wasAbove && horizOverlap && nowIn) {
        p.y = pl.y - p.h;
        p.vy = 0;
        p.onGround = true;
      }
    }
  }

  // Ground
  if (p.y + p.h >= WORLD.groundY) {
    if (isOnGround(p.x, p.w, state.groundSegments)) {
      p.y = WORLD.groundY - p.h;
      p.vy = 0;
      p.onGround = true;
    }
  }
  if (p.onGround) p.didDoubleJump = false;

  // Fell out of world
  if (p.y > WORLD.height + 100) {
    loseLife(state);
    return state;
  }

  // Camera follow
  const targetCam = p.x - 220;
  if (targetCam > state.camera.x) state.camera.x = targetCam;

  // Distance & score
  if (state.camera.x > state.distance) {
    state.distance = Math.floor(state.camera.x);
    state.blockNumber = 1 + Math.floor(state.distance / 1600);
  }
  recomputeScore(state);

  // Coins
  for (const c of state.coins) {
    if (c.collected) continue;
    c.bob += 0.15;
    const yy = c.y + Math.sin(c.bob) * 3;
    const coinBox = { x: c.x - 8, y: yy - 8, w: 16, h: 16 };
    if (aabb(p, coinBox)) {
      c.collected = true;
      const mult = state.tick < p.multiplierUntil ? 2 : 1;
      state.coinsCollected += mult;
      state.pendingSounds.push("coin");
      spawnParticles(state, c.x, c.y, "#ffcc00", 6);
      if (mult > 1) {
        state.floatingTexts.push({
          text: "x2",
          x: c.x,
          y: yy - 18,
          vy: -0.7,
          life: 30,
          color: "#ff003c",
        });
      }
    }
  }

  // Power-ups
  for (const pu of state.powerups) {
    if (pu.collected) continue;
    pu.bob += 0.12;
    const yy = pu.y + Math.sin(pu.bob) * 3;
    const box = { x: pu.x - 2, y: yy - 2, w: 22, h: 22 };
    if (aabb(p, box)) {
      pu.collected = true;
      applyPowerup(state, pu.type, pu.x, yy);
    }
  }

  // Enemies
  for (const e of state.enemies) {
    if (!e.alive) continue;
    e.anim += 0.2;
    if (e.hitFlash > 0) e.hitFlash--;
    const t = ENEMY_TYPES[e.type];

    if (t.isFlying) {
      // Chopper: hover & oscillate horizontally near player
      const dx = (p.x - e.x);
      e.vx = Math.max(-2.4, Math.min(2.4, dx * 0.02 - 1.2));
      e.x += e.vx;
      e.y = e.baseY + Math.sin(e.anim * 0.7) * 16;
    } else {
      e.x += e.vx;
      if (!isOnGround(e.x, e.w, state.groundSegments)) {
        e.vx = -e.vx;
        e.x += e.vx;
      }
    }

    if (aabb(p, e)) {
      const fromAbove = p.vy > 1 && p.y + p.h - p.vy <= e.y + 6;
      if (fromAbove) {
        e.hp--;
        e.hitFlash = 8;
        p.vy = t.isBoss ? -11 : -9;
        spawnParticles(state, e.x + e.w / 2, e.y, t.color, t.isBoss ? 18 : 8);
        if (e.hp <= 0) {
          e.alive = false;
          if (t.isBoss) {
            state.bossesDefeated++;
            state.pendingSounds.push("bossDefeat");
            // Drop a power-up
            const keys = Object.keys(POWERUP_TYPES);
            const type = keys[Math.floor(state.rng() * keys.length)];
            state.powerups.push({
              type,
              x: e.x + e.w / 2 - 9,
              y: e.y + 10,
              collected: false,
              bob: 0,
            });
            state.floatingTexts.push({
              text: `+${t.score} BOSS DOWN`,
              x: e.x + e.w / 2,
              y: e.y - 10,
              vy: -0.5,
              life: 70,
              color: "#39ff14",
              big: true,
            });
          } else {
            state.enemiesDefeated++;
            state.pendingSounds.push("stomp");
            state.floatingTexts.push({
              text: `+${t.score}`,
              x: e.x + e.w / 2,
              y: e.y - 4,
              vy: -0.6,
              life: 36,
              color: "#39ff14",
            });
          }
          // Score bonus tracking via enemiesDefeated/bossesDefeated
        } else {
          state.pendingSounds.push("bossHit");
        }
      } else if (state.tick > p.invulnUntil) {
        loseLife(state);
        return state;
      }
    }
  }

  // Particles
  for (const part of state.particles) {
    part.x += part.vx;
    part.y += part.vy;
    part.vy += 0.3;
    part.life--;
  }
  // Floating text
  for (const ft of state.floatingTexts) {
    ft.x += ft.vx || 0;
    ft.y += ft.vy || 0;
    ft.life--;
  }

  ensureWorldAhead(state);
  pruneBehind(state);
  recomputeScore(state);
  if (state.flashTimer > 0) state.flashTimer--;

  return state;
}

function recomputeScore(state) {
  state.score =
    state.coinsCollected * 10 +
    state.enemiesDefeated * 50 +
    state.bossesDefeated * 500 +
    Math.floor(state.distance / 5);
}

function applyPowerup(state, type, x, y) {
  const p = state.player;
  state.pendingSounds.push("powerup");
  if (state.powerupsByType[type] != null) state.powerupsByType[type]++;
  spawnParticles(state, x + 9, y + 9, POWERUP_TYPES[type].color, 18);
  if (type === "chain") {
    p.hasDoubleJump = true;
    state.floatingTexts.push({
      text: "DOUBLE JUMP",
      x: p.x + p.w / 2,
      y: p.y - 18,
      vy: -0.5,
      life: 60,
      color: "#ffcc00",
    });
  } else if (type === "shades") {
    p.shadesUntil = state.tick + 300;
    p.invulnUntil = Math.max(p.invulnUntil, state.tick + 300);
    state.floatingTexts.push({
      text: "INVINCIBLE 5s",
      x: p.x + p.w / 2,
      y: p.y - 18,
      vy: -0.5,
      life: 60,
      color: "#ffffff",
    });
  } else if (type === "cup") {
    p.multiplierUntil = state.tick + 600;
    state.floatingTexts.push({
      text: "x2 COINS 10s",
      x: p.x + p.w / 2,
      y: p.y - 18,
      vy: -0.5,
      life: 60,
      color: "#ff003c",
    });
  }
}

function loseLife(state) {
  state.pendingSounds.push("hurt");
  state.lives--;
  state.flashTimer = 25;
  state.player.invulnUntil = state.tick + 90;
  state.player.hasDoubleJump = false; // lose double jump on hit
  state.player.didDoubleJump = false;
  state.player.shadesUntil = 0;
  state.player.multiplierUntil = 0;
  if (state.lives <= 0) {
    state.state = "gameover";
    state.pendingSounds.push("gameOver");
    return;
  }
  state.player.x = state.camera.x + 80;
  state.player.y = WORLD.groundY - PLAYER_H - 200;
  state.player.vx = 0;
  state.player.vy = 0;
}

function spawnParticles(state, x, y, color, n = 8) {
  for (let i = 0; i < n; i++) {
    state.particles.push({
      x,
      y,
      vx: state.rng() * 5 - 2.5,
      vy: state.rng() * -3 - 1,
      life: 24,
      color,
    });
  }
}

// ---- Rendering ----
export function render(ctx, state) {
  const { camera } = state;
  ctx.clearRect(0, 0, WORLD.width, WORLD.height);
  drawBackground(ctx, camera);

  ctx.save();
  ctx.translate(-camera.x, 0);

  for (const g of state.groundSegments) {
    ctx.fillStyle = "#1a1a1d";
    ctx.fillRect(g.x, WORLD.groundY, g.w, WORLD.height - WORLD.groundY);
    ctx.fillStyle = "#ff003c";
    ctx.fillRect(g.x, WORLD.groundY - 2, g.w, 2);
    ctx.fillStyle = "rgba(255,204,0,0.18)";
    for (let s = g.x + 20; s < g.x + g.w - 20; s += 60) {
      ctx.fillRect(s, WORLD.groundY + 38, 28, 4);
    }
  }

  for (const pl of state.platforms) {
    ctx.fillStyle = "#2a2a2e";
    ctx.fillRect(pl.x, pl.y + 4, pl.w, pl.h);
    ctx.fillStyle = "#39ff14";
    ctx.fillRect(pl.x, pl.y, pl.w, 4);
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.fillRect(pl.x + 2, pl.y + pl.h + 4, pl.w, 4);
  }

  for (const c of state.coins) {
    if (c.collected) continue;
    const yy = c.y + Math.sin(c.bob) * 3;
    ctx.fillStyle = "#ffcc00";
    ctx.beginPath();
    ctx.arc(c.x, yy, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff5b0";
    ctx.beginPath();
    ctx.arc(c.x - 2, yy - 2, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }

  for (const pu of state.powerups) {
    if (pu.collected) continue;
    drawPowerup(ctx, pu);
  }

  for (const e of state.enemies) {
    if (!e.alive) continue;
    drawEnemy(ctx, e, state.tick);
  }

  drawPlayer(ctx, state.player, state.tick);

  for (const part of state.particles) {
    ctx.globalAlpha = Math.max(0, part.life / 24);
    ctx.fillStyle = part.color;
    ctx.fillRect(part.x, part.y, 3, 3);
  }
  ctx.globalAlpha = 1;

  // Floating texts
  for (const ft of state.floatingTexts) {
    ctx.globalAlpha = Math.max(0, ft.life / 60);
    ctx.fillStyle = ft.color || "#fff";
    ctx.font = `${ft.big ? "bold 22px" : "bold 14px"} 'VT323', monospace`;
    ctx.textAlign = "center";
    ctx.fillText(ft.text, ft.x, ft.y);
  }
  ctx.globalAlpha = 1;
  ctx.textAlign = "left";

  ctx.restore();

  // Boss HP bar (screen-space, only if active)
  if (state.bossActive && state.bossActive.alive) {
    drawBossHpBar(ctx, state.bossActive);
  }

  if (state.flashTimer > 0) {
    ctx.fillStyle = `rgba(255,0,60,${state.flashTimer / 50})`;
    ctx.fillRect(0, 0, WORLD.width, WORLD.height);
  }
}

function drawBackground(ctx, camera) {
  const g = ctx.createLinearGradient(0, 0, 0, WORLD.height);
  g.addColorStop(0, "#15060f");
  g.addColorStop(0.6, "#08020a");
  g.addColorStop(1, "#000000");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, WORLD.width, WORLD.height);

  const farOffset = -((camera.x * 0.2) % 200);
  ctx.fillStyle = "#0d0d12";
  for (let x = farOffset - 200; x < WORLD.width + 200; x += 200) {
    const h = 180 + ((x * 13) % 60);
    ctx.fillRect(x, WORLD.groundY - h, 120, h);
    ctx.fillStyle = "rgba(255,204,0,0.08)";
    for (let wy = WORLD.groundY - h + 20; wy < WORLD.groundY - 20; wy += 18) {
      for (let wx = x + 14; wx < x + 100; wx += 18) {
        if ((wx + wy) % 5 < 2) ctx.fillRect(wx, wy, 6, 8);
      }
    }
    ctx.fillStyle = "#0d0d12";
  }

  const midOffset = -((camera.x * 0.45) % 240);
  ctx.fillStyle = "#161018";
  for (let x = midOffset - 240; x < WORLD.width + 240; x += 240) {
    const h = 260 + ((x * 7) % 80);
    ctx.fillRect(x, WORLD.groundY - h, 160, h);
    if (((x / 240) | 0) % 3 === 0) {
      ctx.fillStyle = "#ff003c";
      ctx.fillRect(x + 30, WORLD.groundY - h + 40, 80, 6);
      ctx.fillStyle = "#161018";
    }
  }

  const closeOffset = -((camera.x * 0.8) % 320);
  ctx.fillStyle = "#000";
  for (let x = closeOffset - 320; x < WORLD.width + 320; x += 320) {
    ctx.fillRect(x + 40, WORLD.groundY - 200, 4, 200);
    ctx.fillRect(x + 30, WORLD.groundY - 200, 24, 4);
    ctx.fillStyle = "rgba(255,204,0,0.15)";
    ctx.beginPath();
    ctx.arc(x + 42, WORLD.groundY - 195, 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#000";
  }
}

function drawPlayer(ctx, p, tick) {
  const x = p.x;
  const y = p.y;
  const blink = p.invulnUntil > tick && tick % 6 < 3;
  if (blink) ctx.globalAlpha = 0.5;

  // Aura while invulnerable from shades
  if (p.shadesUntil > tick) {
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = "#39ff14";
    ctx.beginPath();
    ctx.arc(x + p.w / 2, y + p.h / 2, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = blink ? 0.5 : 1;
  }

  // Body — black hoodie
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(x + 4, y + 14, p.w - 8, p.h - 18);
  // Hood
  ctx.fillStyle = "#161616";
  ctx.fillRect(x + 2, y + 6, p.w - 4, 14);
  // Face
  ctx.fillStyle = "#5b3a22";
  ctx.fillRect(x + 8, y + 12, p.w - 16, 8);
  // Shades overlay if active
  if (p.shadesUntil > tick) {
    ctx.fillStyle = "#000";
    ctx.fillRect(x + 7, y + 13, p.w - 14, 4);
    ctx.fillStyle = "#39ff14";
    ctx.fillRect(x + 9, y + 14, 2, 1);
    ctx.fillRect(x + p.w - 11, y + 14, 2, 1);
  } else {
    ctx.fillStyle = "#ff003c";
    const eyeX = p.facing === 1 ? x + 16 : x + 8;
    ctx.fillRect(eyeX, y + 14, 3, 2);
  }
  // Chain
  ctx.fillStyle = p.hasDoubleJump ? "#ffcc00" : "#ffcc00";
  ctx.fillRect(x + p.w / 2 - 4, y + 22, 8, 2);
  ctx.fillRect(x + p.w / 2 - 1, y + 24, 2, 4);
  // Legs — sweat pants color flips to red mult during ×2
  const multActive = p.multiplierUntil > tick;
  const legAnim = p.onGround && Math.abs(p.vx) > 0.2 ? Math.sin(tick * 0.4) * 3 : 0;
  ctx.fillStyle = multActive ? "#ff003c" : "#ff003c";
  ctx.fillRect(x + 6, y + p.h - 12, 6, 12 + legAnim);
  ctx.fillRect(x + p.w - 12, y + p.h - 12, 6, 12 - legAnim);
  // Sneakers
  ctx.fillStyle = "#fff";
  ctx.fillRect(x + 5, y + p.h - 4, 8, 4);
  ctx.fillRect(x + p.w - 13, y + p.h - 4, 8, 4);

  // Double-jump dust marker (gold ring) when ability available and airborne
  if (p.hasDoubleJump && !p.didDoubleJump && !p.onGround) {
    ctx.strokeStyle = "rgba(255,204,0,0.6)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x + p.w / 2, y + p.h + 4, 12, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 1;
  }

  ctx.globalAlpha = 1;
}

function drawEnemy(ctx, e, tick) {
  const t = ENEMY_TYPES[e.type];
  if (e.hitFlash > 0) ctx.globalAlpha = 0.6;

  if (e.type === "snake") {
    ctx.fillStyle = t.color;
    for (let i = 0; i < e.w; i += 4) {
      const yo = Math.sin(e.anim + i * 0.3) * 2;
      ctx.fillRect(e.x + i, e.y + yo, 4, e.h);
    }
    ctx.fillStyle = t.accent;
    ctx.fillRect(e.x - 2, e.y - 2, 8, e.h + 4);
    ctx.fillStyle = "#ff003c";
    ctx.fillRect(e.x, e.y, 2, 2);
    ctx.globalAlpha = 1;
    return;
  }

  if (e.type === "swatvan") {
    // SWAT VAN: wide armored vehicle
    // Body
    ctx.fillStyle = t.color;
    ctx.fillRect(e.x, e.y + 8, e.w, e.h - 8);
    // Roof
    ctx.fillStyle = t.accent;
    ctx.fillRect(e.x + 8, e.y, e.w - 16, 12);
    // Light bar (flashing red/blue)
    const flash = (tick / 6) | 0;
    ctx.fillStyle = flash % 2 === 0 ? "#ff003c" : "#1a3b8e";
    ctx.fillRect(e.x + e.w / 2 - 14, e.y - 4, 14, 4);
    ctx.fillStyle = flash % 2 === 0 ? "#1a3b8e" : "#ff003c";
    ctx.fillRect(e.x + e.w / 2, e.y - 4, 14, 4);
    // Windows
    ctx.fillStyle = "#0d1014";
    ctx.fillRect(e.x + 14, e.y + 14, 16, 10);
    ctx.fillRect(e.x + e.w - 30, e.y + 14, 16, 10);
    // "SWAT" stencil
    ctx.fillStyle = "#fff";
    ctx.font = "bold 10px monospace";
    ctx.fillText("SWAT", e.x + e.w / 2 - 12, e.y + 36);
    // Wheels
    ctx.fillStyle = "#0a0a0a";
    const wob = Math.sin(e.anim * 2) * 1;
    ctx.beginPath();
    ctx.arc(e.x + 16, e.y + e.h - 2 + wob, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(e.x + e.w - 16, e.y + e.h - 2 - wob, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    return;
  }

  if (e.type === "chopper") {
    // CHOPPER: hovering attack helicopter
    // Body
    ctx.fillStyle = t.color;
    ctx.fillRect(e.x + 14, e.y + 8, e.w - 24, e.h - 12);
    // Cockpit
    ctx.fillStyle = "#0d1014";
    ctx.fillRect(e.x + 14, e.y + 12, 18, 8);
    ctx.fillStyle = "#39ff14";
    ctx.fillRect(e.x + 16, e.y + 14, 2, 2);
    // Tail
    ctx.fillStyle = t.accent;
    ctx.fillRect(e.x + e.w - 16, e.y + 16, 14, 5);
    ctx.fillRect(e.x + e.w - 4, e.y + 12, 4, 14);
    // Skids
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(e.x + 18, e.y + e.h - 2, e.w - 32, 2);
    // Rotor (blurred line)
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    const rotorW = Math.abs(Math.cos(e.anim * 3)) * e.w + 20;
    ctx.fillRect(e.x + e.w / 2 - rotorW / 2, e.y - 2, rotorW, 2);
    // Light
    ctx.fillStyle = (tick / 4) | 0 % 2 === 0 ? "#ff003c" : "#ffcc00";
    ctx.fillRect(e.x + e.w / 2 - 2, e.y + e.h - 4, 4, 4);
    ctx.globalAlpha = 1;
    return;
  }

  // Bipedal walking enemies
  ctx.fillStyle = t.color;
  ctx.fillRect(e.x + 4, e.y + 10, e.w - 8, e.h - 14);
  ctx.fillStyle = t.accent;
  ctx.fillRect(e.x + 6, e.y, e.w - 12, 12);

  if (e.type === "soldier") {
    ctx.fillStyle = "#39ff14";
    ctx.fillRect(e.x + 8, e.y + 2, e.w - 16, 2);
    ctx.fillStyle = "#222";
    ctx.fillRect(e.x - 6, e.y + 18, 10, 3);
  } else if (e.type === "police") {
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(e.x + 4, e.y + 6, e.w - 8, 3);
    ctx.fillStyle = "#ffcc00";
    ctx.fillRect(e.x + e.w / 2 - 2, e.y + 14, 4, 4);
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(e.x - 4, e.y + 22, 8, 2);
  } else if (e.type === "thug") {
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(e.x + 4, e.y + 5, e.w - 8, 4);
    ctx.fillStyle = "#ffcc00";
    ctx.fillRect(e.x + e.w / 2 - 3, e.y + 14, 6, 2);
  }

  ctx.fillStyle = t.accent;
  const wob = Math.sin(e.anim) * 2;
  ctx.fillRect(e.x + 6, e.y + e.h - 6, 6, 6 + wob);
  ctx.fillRect(e.x + e.w - 12, e.y + e.h - 6, 6, 6 - wob);
  ctx.globalAlpha = 1;
}

function drawPowerup(ctx, pu) {
  const yy = pu.y + Math.sin(pu.bob) * 3;
  const t = POWERUP_TYPES[pu.type];

  // Halo
  ctx.globalAlpha = 0.35;
  ctx.fillStyle = t.color;
  ctx.beginPath();
  ctx.arc(pu.x + 9, yy + 9, 14, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  if (pu.type === "chain") {
    // Gold chain icon
    ctx.fillStyle = "#ffcc00";
    for (let i = 0; i < 4; i++) {
      ctx.fillRect(pu.x + i * 4, yy + (i % 2 === 0 ? 2 : 10), 4, 6);
    }
    ctx.strokeStyle = "#fff5b0";
    ctx.lineWidth = 1;
    ctx.strokeRect(pu.x, yy + 2, 18, 14);
  } else if (pu.type === "shades") {
    // Sunglasses
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(pu.x, yy + 5, 8, 7);
    ctx.fillRect(pu.x + 10, yy + 5, 8, 7);
    ctx.fillRect(pu.x + 8, yy + 7, 2, 2);
    ctx.fillStyle = "#39ff14";
    ctx.fillRect(pu.x + 2, yy + 7, 2, 2);
    ctx.fillRect(pu.x + 12, yy + 7, 2, 2);
  } else if (pu.type === "cup") {
    // Red solo cup
    ctx.fillStyle = "#ff003c";
    ctx.beginPath();
    ctx.moveTo(pu.x, yy + 3);
    ctx.lineTo(pu.x + 18, yy + 3);
    ctx.lineTo(pu.x + 15, yy + 18);
    ctx.lineTo(pu.x + 3, yy + 18);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.fillRect(pu.x + 1, yy + 3, 16, 2);
    ctx.fillStyle = "#7a0020";
    ctx.fillRect(pu.x + 3, yy + 5, 12, 2);
  }
}

function drawBossHpBar(ctx, boss) {
  const t = ENEMY_TYPES[boss.type];
  const barW = 360;
  const x = (WORLD.width - barW) / 2;
  const y = 22;
  ctx.fillStyle = "rgba(0,0,0,0.7)";
  ctx.fillRect(x - 6, y - 6, barW + 12, 22);
  ctx.strokeStyle = "#ff003c";
  ctx.lineWidth = 2;
  ctx.strokeRect(x - 6, y - 6, barW + 12, 22);
  ctx.lineWidth = 1;

  const ratio = Math.max(0, boss.hp / boss.maxHp);
  ctx.fillStyle = "#ff003c";
  ctx.fillRect(x, y, barW * ratio, 10);

  ctx.fillStyle = "#fff";
  ctx.font = "bold 12px 'VT323', monospace";
  ctx.textAlign = "center";
  ctx.fillText(`${t.label} — ${boss.hp}/${boss.maxHp}`, WORLD.width / 2, y - 10);
  ctx.textAlign = "left";
}
